class MiniCart extends HTMLElement {
  constructor() {
    super();
    this.mutationOpenedDrawer = false;
    this.savingsRequestId = 0;
    this.savingsLoadPromise = null;
    this.savingsReloadQueued = false;
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');

        this.loadCartSavings();
        document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) {
      return;
    }
    
    this.drawer.openMenuDrawer();
    requestAnimationFrame(() => this.loadCartSavings());
  }

  beginMutation(activeElement) {
    if (activeElement) this.setActiveElement(activeElement);
    this.mutationOpenedDrawer = !this.isDrawerOpen();
    this.showMutationPlaceholder();
    this.open();
  }

  endMutation() {
    this.removeMutationPlaceholder();
    this.mutationOpenedDrawer = false;
  }

  cancelMutation() {
    const shouldClose = this.mutationOpenedDrawer;
    this.removeMutationPlaceholder();
    this.mutationOpenedDrawer = false;

    if (shouldClose && this.isDrawerOpen()) this.drawer.closeMenuDrawer();
  }

  isDrawerOpen() {
    const detailsElement = this.drawer?.querySelector('details');
    return !!detailsElement?.hasAttribute('open')
      && (document.body.classList.contains('mini-cart--open') || document.body.classList.contains('mini-cart--opening'));
  }

  showMutationPlaceholder() {
    this.removeMutationPlaceholder();

    const navigation = this.querySelector('cart-items > .mini-cart__navigation');
    const placeholder = document.createElement(navigation ? 'li' : 'div');
    placeholder.className = 'mini-cart__pending-product';
    placeholder.dataset.cartPendingItem = '';
    placeholder.setAttribute('role', 'status');
    placeholder.setAttribute('aria-label', 'Adding item to cart');

    const container = document.createElement('span');
    container.className = 'mini-cart__pending-container';

    const image = document.createElement('span');
    image.className = 'mini-cart__pending-image';

    const description = document.createElement('span');
    description.className = 'mini-cart__pending-description';

    const content = document.createElement('span');
    content.className = 'mini-cart__pending-content';
    ['title', 'price'].forEach((part) => {
      const line = document.createElement('span');
      line.className = `mini-cart__pending-line mini-cart__pending-line--${part}`;
      content.appendChild(line);
    });

    const option = document.createElement('span');
    option.className = 'mini-cart__pending-line mini-cart__pending-line--option';

    const actions = document.createElement('span');
    actions.className = 'mini-cart__pending-actions';
    const quantity = document.createElement('span');
    quantity.className = 'mini-cart__pending-quantity';
    const remove = document.createElement('span');
    remove.className = 'mini-cart__pending-remove';
    actions.append(quantity, remove);

    description.append(content, option, actions);
    container.append(image, description);
    placeholder.appendChild(container);

    if (navigation) {
      navigation.prepend(placeholder);
      return;
    }

    const emptyState = this.querySelector('.mini-cart__empty');
    const cartForm = this.querySelector('form.mini-cart');
    if (emptyState) {
      placeholder.classList.add('mini-cart__pending-product--empty');
      cartForm?.classList.add('is-mutation-pending');
      const header = this.querySelector('.mini-cart__header');
      if (header) {
        header.after(placeholder);
      } else {
        emptyState.prepend(placeholder);
      }
      return;
    }

    placeholder.classList.add('mini-cart__pending-product--shell');
    this.classList.add('is-mutation-pending');
    this.appendChild(placeholder);
  }

  removeMutationPlaceholder() {
    this.querySelector('[data-cart-pending-item]')?.remove();
    this.classList.remove('is-mutation-pending');
    this.querySelector('form.mini-cart')?.classList.remove('is-mutation-pending');
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      if (document.getElementById(section.id)) {
        document.getElementById(section.id).innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    }));

    this.renderCartProgress(parsedState.sections?.['cart-progress']);
    this.endMutation();
    this.open();
    this.loadCartSavings();
  }

  loadCartSavings() {
    if (this.savingsLoadPromise) {
      this.savingsReloadQueued = true;
      return this.savingsLoadPromise;
    }

    const placeholders = Array.from(this.querySelectorAll('[data-cart-savings-placeholder]'));
    if (!placeholders.length) return Promise.resolve(false);

    const requests = new Map();
    placeholders.forEach((placeholder) => {
      const url = placeholder.dataset.url;
      if (!url) return;

      placeholder.dataset.loading = 'true';
      if (!requests.has(url)) requests.set(url, []);
      requests.get(url).push(placeholder);
    });

    if (!requests.size) return Promise.resolve(false);

    const requestId = ++this.savingsRequestId;
    this.savingsLoadPromise = Promise.all(Array.from(requests.entries()).map(([url, urlPlaceholders]) => (
      fetch(url)
        .then((response) => response.text())
        .then((html) => {
          if (requestId !== this.savingsRequestId) return false;

          const content = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-cart-savings-content]');

          urlPlaceholders.forEach((placeholder) => {
            if (!placeholder.isConnected) return;
            placeholder.removeAttribute('data-loading');
            placeholder.replaceChildren();
            if (content) placeholder.appendChild(content.cloneNode(true));
          });

          return !!content;
        })
    )))
      .catch((error) => {
        console.error('Error loading cart savings:', error);
        return false;
      })
      .finally(() => {
        this.savingsLoadPromise = null;
        if (this.savingsReloadQueued) {
          this.savingsReloadQueued = false;
          queueMicrotask(() => this.loadCartSavings());
        }
      });

    return this.savingsLoadPromise;
  }

  renderCartProgress(sectionHTML) {
    const placeholder = this.querySelector('[data-cart-progress-placeholder]');
    if (!placeholder || sectionHTML === undefined) return;

    const progress = new DOMParser()
      .parseFromString(sectionHTML, 'text/html')
      .querySelector('.cart-progress');

    if (progress) {
      placeholder.replaceWith(progress);
    } else {
      placeholder.remove();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-progress',
        section: 'cart-progress',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('mini-cart', MiniCart);
